import * as React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Play, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';
import { actionsApi } from '@/api/actions';
import { unwrapError } from '@/api/client';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import type { DiagnoseInput, DiagnoseResponse, Language } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DecisionCard } from './DecisionCard';
import { DEFAULT_LANGUAGE, LanguageSelect } from './LanguageSelect';
import { PromptVersionSelect } from './PromptVersionSelect';

export function DiagnosePanel({
  initialPromptVersion,
}: {
  initialPromptVersion?: number | null;
}): JSX.Element {
  const { t } = useTranslation();
  const { currentBotId, currentBot } = useWorkspace();
  const [shipmentId, setShipmentId] = React.useState('');
  const [orderId, setOrderId] = React.useState('');
  const [trackingNumber, setTrackingNumber] = React.useState('');
  const [context, setContext] = React.useState('');
  const [language, setLanguage] = React.useState<Language>(DEFAULT_LANGUAGE);
  const [promptVersion, setPromptVersion] = React.useState<number | null>(initialPromptVersion ?? null);
  const [result, setResult] = React.useState<DiagnoseResponse | null>(null);

  const mutation = useMutation({
    mutationFn: (input: DiagnoseInput) => actionsApi.diagnose(input, language),
    onSuccess: (data) => {
      setResult(data);
      if (data.decision.kind === 'failed')
        toast.error(t('chat.playground.failedToast', { reason: data.decision.reason }));
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  if (!currentBotId) {
    return (
      <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
        {t('common.selectBotInTopbar')}
      </div>
    );
  }

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!shipmentId.trim() && !orderId.trim() && !trackingNumber.trim()) {
      toast.error(t('chat.diagnose.atLeastOneError'));
      return;
    }
    mutation.mutate({
      ...(shipmentId.trim() ? { shipmentId: shipmentId.trim() } : {}),
      ...(orderId.trim() ? { orderId: orderId.trim() } : {}),
      ...(trackingNumber.trim() ? { trackingNumber: trackingNumber.trim() } : {}),
      ...(context.trim() ? { context: context.trim() } : {}),
      ...(promptVersion !== null ? { promptVersion } : {}),
    });
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Stethoscope className="h-4 w-4" />
            {t('chat.diagnose.title')}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            <Trans
              i18nKey="chat.diagnose.subtitle"
              values={{ bot: currentBot?.name ?? currentBotId }}
              components={{ mono: <span className="font-mono" />, code: <code /> }}
            />
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="dg-shipment">{t('chat.diagnose.shipmentId')}</Label>
              <Input
                id="dg-shipment"
                value={shipmentId}
                onChange={(e) => setShipmentId(e.target.value)}
                placeholder="SHP-12345"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="dg-order">{t('chat.diagnose.orderId')}</Label>
                <Input
                  id="dg-order"
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  placeholder="ORD-789"
                />
              </div>
              <div>
                <Label htmlFor="dg-track">{t('chat.diagnose.trackingNumber')}</Label>
                <Input
                  id="dg-track"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="1Z…"
                />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">{t('chat.diagnose.atLeastOne')}</p>
            <div>
              <Label htmlFor="dg-ctx">{t('chat.diagnose.context')}</Label>
              <Textarea
                id="dg-ctx"
                rows={4}
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder={t('chat.diagnose.contextPlaceholder')}
              />
            </div>
            <LanguageSelect value={language} onChange={setLanguage} />
            <PromptVersionSelect value={promptVersion} onChange={setPromptVersion} />
            <div className="flex justify-end">
              <Button type="submit" disabled={mutation.isPending} className="gap-2">
                {mutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                {t('chat.diagnose.run')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {mutation.isPending ? (
          <Card>
            <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('chat.diagnose.orchestratorRunning')}
            </CardContent>
          </Card>
        ) : result ? (
          <DecisionCard
            decision={result.decision}
            usage={result.usage}
            latencyMs={result.latencyMs}
            sessionId={result.sessionId}
          />
        ) : (
          <Card>
            <CardContent className="p-6 text-sm text-muted-foreground">
              {t('chat.diagnose.resultPlaceholder')}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
